import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import {
  Application,
  Assets,
  Cache,
  Container,
  Graphics,
  Sprite,
} from "pixi.js";
import { CompleteBattleLog } from "types/battle-engine";
import {
  gridMarginX,
  gridMarginY,
  gridTileSize,
  GRID_WIDTH,
  GRID_HEIGHT,
} from "types/battle-engine/constants";
import { MenuScreen } from "@/components/battle/MenuScreen";
import { BattleScreen } from "@/components/battle/BattleScreen";
import {
  buildAssetManifest,
  preloadBattleAssets,
  resetBattleAssets,
} from "@/lib/battle/asset-loader";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { useDuel } from "@/providers/duel-provider";
import { DuelBattleState, DuelState } from "types/duels/types";
import { isXsScreen } from "@/utils/screen-size-check";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { EnvironmentalEffects } from "@/components/environment/environmental-effects";

type Screen = "menu" | "loading" | "battle";

const Battle: React.FC = () => {
  const [screen, setScreen] = useState<Screen>("menu");
  const [app, setApp] = useState<Application | null>(null);
  const [world, setWorld] = useState<Container | null>(null);
  const [overlayWorld, setOverlayWorld] = useState<Container | null>(null);
  const [pokemons, setPokemons] = useState<Record<string, ClientPokemon>>({});
  const [battleLog, setBattleLog] = useState<CompleteBattleLog | null>(null);
  const [battleStarted, setBattleStarted] = useState(false);
  const [battleKey, setBattleKey] = useState(0);
  const [currentBattleUrl, setCurrentBattleUrl] = useState<string | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const isLocalTest = urlParams.get("localTest") === "true";

  const { duelState, setDuelState } = useDuel();

  // Check for local test mode on component mount
  useEffect(() => {
    if (!isLocalTest) return;

    const urlParams = new URLSearchParams(window.location.search);
    const battleLogPath = urlParams.get("battleLog");

    if (!battleLogPath) throw new Error("Battle log path is required");

    setScreen("loading");
    void loadLocalBattleLog(battleLogPath);
  }, []);

  useEffect(() => {
    if (isLocalTest) return;

    console.log(
      "[Battle] battle log url",
      duelState.current_battle?.battle_log_url,
    );
    console.log("[Battle] status", duelState.status);

    // Reset to menu when leaving battle (back to drafting phase)
    // This prepares Battle for the next round
    if (
      duelState.status !== "in_battle" &&
      duelState.status !== "battle_completed" &&
      duelState.status !== "battle_starting"
    ) {
      if (screen === "battle") {
        console.log("[Battle] Leaving battle, resetting to menu");
        setScreen("menu");
        setBattleStarted(false);
        setBattleLog(null);
      }
      return;
    }

    if (
      duelState.status === "in_battle" &&
      duelState.current_battle?.battle_log_url
    ) {
      const newUrl = duelState.current_battle.battle_log_url;

      // Detect new round: different battle URL means new battle
      if (newUrl !== currentBattleUrl) {
        console.log("[Battle] New battle detected, resetting state");
        setCurrentBattleUrl(newUrl);

        // Clean up world for new battle (keep app alive)
        if (world) {
          while (world.children.length > 0) {
            const child = world.children[0];
            if (child) {
              world.removeChild(child);
              child.destroy({ children: true });
            }
          }
        }

        if (overlayWorld) {
          while (overlayWorld.children.length > 0) {
            const child = overlayWorld.children[0];
            if (child) {
              overlayWorld.removeChild(child);
              child.destroy({ children: true });
            }
          }
        }

        setPokemons({});
        setBattleStarted(false);
        setBattleLog(null); // Clear old battle log to prevent race condition
        setBattleKey((k) => k + 1);
      }

      setScreen("loading");
      void getBattleLog(newUrl);
    }
  }, [
    duelState.status,
    duelState.current_battle,
    isLocalTest,
    currentBattleUrl,
    world,
    overlayWorld,
    screen,
  ]);

  const getBattleLog = async (s3Url: string) => {
    const response = await fetch(s3Url);
    const data = (await response.json()) as CompleteBattleLog;

    setBattleLog(data);
  };

  const loadLocalBattleLog = async (battleLogPath: string) => {
    try {
      console.log(`[Battle] Loading local battle log from: ${battleLogPath}`);
      const response = await fetch(battleLogPath);
      const data = await response.json();

      setBattleLog(data);

      // Mock the duel state for local testing
      const mockBattleState: DuelBattleState = {
        round_index: 1,
        your_team: data.teamA.members.map((member: any) => ({
          id: member.pokemon.id,
          name: member.pokemon.name,
          base_pokemon_name: member.pokemon.base_pokemon_name,
          level: member.pokemon.level,
          display_id: member.pokemon.display_id,
        })),
        opponent_team: data.teamB.members.map((member: any) => ({
          id: member.pokemon.id,
          name: member.pokemon.name,
          base_pokemon_name: member.pokemon.base_pokemon_name,
          level: member.pokemon.level,
          display_id: member.pokemon.display_id,
        })),
        battle_log_url: battleLogPath,
      };

      setDuelState((prev) => ({
        ...prev,
        status: "in_battle",
        current_battle: mockBattleState,
      }));

      setScreen("menu");

      if (!battleStarted) {
        setBattleStarted(true);
        handleStartBattle(data);
      }
    } catch (error) {
      console.error("Failed to load local battle log:", error);
      setScreen("menu");
    }
  };

  useEffect(() => {
    if (!battleLog) return;
    // Update winner on battle state

    let winner: DuelBattleState["winner"] = undefined;
    if (battleLog.winner === "teamA") winner = "player_a";
    if (battleLog.winner === "teamB") winner = "player_b";

    if (winner && duelState.current_battle)
      duelState.current_battle.winner = winner;

    if (battleStarted) return;
    setBattleStarted(true);
    handleStartBattle(battleLog);
  }, [battleLog, battleStarted]);

  // Initialize PixiJS application
  useEffect(() => {
    let localApp: Application | null = null;
    let isCancelled = false;

    // Helper to cleanup app if cancelled mid-init
    const cleanupIfCancelled = () => {
      if (isCancelled && localApp) {
        console.log("Cleaning up cancelled app initialization...");
        try {
          localApp.destroy(true);
        } catch (e) {
          console.warn("Error cleaning up cancelled app:", e);
        }
        localApp = null;
      }
      return isCancelled;
    };

    const initApp = async () => {
      try {
        console.log("Starting PixiJS initialization...");

        if (isCancelled) {
          console.log("Init cancelled (1)");
          return;
        }

        const gameContainer = document.getElementById("game-container");
        if (!gameContainer) {
          console.error("Game container not found!");
          return;
        }

        // Check for existing canvas INSIDE the game container only
        const existingCanvas = gameContainer.querySelector("canvas");
        if (existingCanvas) {
          console.log("Canvas already exists in game container, skipping init");
          return;
        }

        // Create a PixiJS application
        console.log("Creating PixiJS Application...");
        localApp = new Application();

        if (cleanupIfCancelled()) {
          console.log("Init cancelled (2)");
          return;
        }

        // Try to load font, but don't fail if it doesn't exist
        try {
          console.log("Loading font...");
          await document.fonts?.load?.('16px "vck"');
          console.log("Font loaded successfully");
        } catch (fontError) {
          console.warn("Font loading failed, continuing anyway:", fontError);
        }

        if (cleanupIfCancelled()) {
          console.log("Init cancelled (3)");
          return;
        }

        // Initialize the application
        console.log("Initializing PixiJS app...");
        await localApp.init({
          background: "#161e29",
          resizeTo: gameContainer,
          antialias: false,
        });
        console.log("PixiJS app initialized");

        // Check again after async init
        if (cleanupIfCancelled()) {
          console.log("Init cancelled (4 - after app.init)");
          return;
        }

        // Add canvas to DOM
        gameContainer.appendChild(localApp.canvas);

        localApp.ticker.add((time) => {
          // Game loop updates happen here
        });

        localApp.stage.sortableChildren = true;

        // Create world container for camera control
        console.log("Creating world container...");
        const worldContainer = new Container();
        worldContainer.sortableChildren = true;
        localApp.stage.addChild(worldContainer);

        // Create overlay container (static, on top of world)
        const overlayContainer = new Container();
        overlayContainer.sortableChildren = true;
        localApp.stage.addChild(overlayContainer);

        // Calculate camera settings to center and fit grid
        const gridWidth = GRID_WIDTH * gridTileSize;
        const gridHeight = GRID_HEIGHT * gridTileSize;

        const gridCenterX = gridMarginX + gridWidth / 2;
        const gridCenterY = gridMarginY + gridHeight / 2;

        // Calculate scale to fit grid (with padding) on screen
        const targetWidth = gridWidth;
        const targetHeight = gridHeight;
        const scaleX = gameContainer.clientWidth / targetWidth;
        const scaleY = gameContainer.clientHeight / targetHeight;
        const scale = Math.min(scaleX, scaleY, 1); // Never scale up, only down

        // Apply scale and center the grid
        worldContainer.scale.set(scale);
        worldContainer.position.set(
          gameContainer.clientWidth / 2 - gridCenterX * scale,
          gameContainer.clientHeight / 2 - gridCenterY * scale,
        );

        overlayContainer.scale.set(scale);
        overlayContainer.position.set(
          gameContainer.clientWidth / 2 - gridCenterX * scale,
          gameContainer.clientHeight / 2 - gridCenterY * scale,
        );

        console.log("PixiJS initialization complete!");
        setApp(localApp);
        setWorld(worldContainer);
        setOverlayWorld(overlayContainer);
      } catch (error) {
        console.error("Failed to initialize PixiJS:", error);
      }
    };

    initApp();

    return () => {
      isCancelled = true;
      if (localApp) {
        try {
          console.log("Starting PixiJS cleanup...");

          // Stop the ticker first to prevent new render calls
          if (localApp.ticker) {
            localApp.ticker.stop();
          }

          // Synchronous cleanup - no setTimeout to avoid race conditions with new component mounting
          try {
            // Remove all children from stage first
            // if (localApp.stage && !localApp.stage.destroyed) {
            //   while (localApp.stage.children.length > 0) {
            //     try {
            //       const child = localApp.stage.children[0];
            //       if (child) {
            //         localApp.stage.removeChild(child);
            //         child.destroy({ children: true, texture: true });
            //       }
            //     } catch (childError) {
            //       console.warn("Error removing stage child:", childError);
            //       break; // Prevent infinite loop
            //     }
            //   }
            // }

            // Remove canvas from DOM
            if (localApp.canvas && localApp.canvas.remove) {
              console.log("[CLEANUP] Removing canvas");
              localApp.canvas.remove();
            }

            // Destroy stage with textures
            if (localApp.stage && localApp.stage.destroy) {
              console.log("[CLEANUP] Destroying stage");
              localApp.stage.destroy({ children: true, texture: true });
            }

            // Stop and destroy app
            if (localApp.stop) {
              console.log("[CLEANUP] Stopping app");
              localApp.stop();
            }
            if (localApp.destroy) {
              console.log("[CLEANUP] Destroying app");
              localApp.destroy(true, { children: true, texture: true });
            }

            // CRITICAL: Reset PixiJS Assets cache to prevent stale texture references
            // resetBattleAssets();

            localApp = null;
            setApp(null);
            setPokemons({});
            setWorld(null);
            setOverlayWorld(null);
            console.log("PixiJS cleanup complete (with Assets.reset)");
          } catch (error) {
            console.warn("Error during PixiJS cleanup:", error);
          }
        } catch (error) {
          console.warn("Error during PixiJS cleanup:", error);
        }
      }
    };
  }, []);

  // Handle battle start from menu
  const handleStartBattle = useCallback(
    async (battleLog: CompleteBattleLog) => {
      if (!app || !world) return;

      console.log("Starting battle setup...");

      try {
        // Build asset manifest
        const manifest = buildAssetManifest(
          battleLog.teamA.members.map((member) => member.pokemon),
          battleLog.teamB.members.map((member) => member.pokemon),
        );

        // Load everything in parallel
        await Promise.all([
          // Load assets
          preloadBattleAssets(manifest, (progress) => {
            console.log(`Loading progress: ${progress.percentage.toFixed(1)}%`);
          }),
        ]);

        console.log("Assets loaded, setting up game scene...");

        // Setup zoomed-out camera for loading screen
        const gameContainer = document.getElementById("game-container");
        if (!gameContainer) throw new Error("Game container not found");

        // Phase 2: Setup game scene (map, grid, pokemons)
        let mapName =
          battleLog.mapId ?? `map (${1 + Math.floor(Math.random() * 65)})`;

        // Helper to load asset only if not already cached
        const loadAssetIfNeeded = async (path: string) => {
          if (Cache.has(path)) {
            return Cache.get(path);
          }
          return Assets.load(path);
        };

        // Load map sprite

        let mapImage: any = null;

        mapName = "t3";

        try {
          const mapPath = `/assets/maps/${mapName}.webp`;
          mapImage = await loadAssetIfNeeded(mapPath);
        } catch (error) {
          const mapPath = `/assets/maps/${mapName}.png`;
          mapImage = await loadAssetIfNeeded(mapPath);
        }

        if (!mapImage) throw new Error("Failed to load map");

        const mapSprite = new Sprite(mapImage);
        const mapSpriteBack = new Sprite(mapImage);

        // Position map at grid center instead of container center
        const mapGridWidth = GRID_WIDTH * gridTileSize;
        const mapGridHeight = GRID_HEIGHT * gridTileSize;
        const mapGridCenterX = gridMarginX + mapGridWidth / 2;
        const mapGridCenterY = gridMarginY + mapGridHeight / 2;

        mapSprite.x = mapGridCenterX;
        mapSprite.y = mapGridCenterY;
        const mapScale = 1;
        mapSprite.scale.set(mapScale);
        mapSprite.anchor.set(0.5, 0.5);
        mapSprite.rotation = 0;
        mapSprite.zIndex = -1000;
        mapSprite.sortableChildren = true;
        mapSprite.alpha = 1;
        world.addChild(mapSprite);

        // Map overlay
        const overlayPath = `/assets/maps/overlay/default.webp`;
        const mapOverlay = await loadAssetIfNeeded(overlayPath);
        const mapOverlaySprite = new Sprite(mapOverlay);
        mapOverlaySprite.x = mapGridCenterX;
        mapOverlaySprite.y = mapGridCenterY;
        mapOverlaySprite.scale.set(1, 1);
        mapOverlaySprite.anchor.set(0.5, 0.5);
        mapOverlaySprite.rotation = 0;
        mapOverlaySprite.zIndex = 9999;
        mapOverlaySprite.alpha = 1;
        mapOverlaySprite.sortableChildren = false;

        if (overlayWorld) {
          overlayWorld.addChild(mapOverlaySprite);
        } else {
          world.addChild(mapOverlaySprite);
        }

        // Draw the grid
        const grid = new Graphics();
        grid.alpha = 0;

        for (let y = 0; y < GRID_HEIGHT; y++) {
          for (let x = 0; x < GRID_WIDTH; x++) {
            const isEven = (x + y) % 2 === 0;
            const color = isEven ? 0x660000 : 0x000066;

            grid.rect(
              gridMarginX + x * gridTileSize,
              gridMarginY + y * gridTileSize,
              gridTileSize,
              gridTileSize,
            );
            grid.fill(color);
          }
        }

        grid.zIndex = -500;
        world.addChild(grid);

        const gridWidth = GRID_WIDTH * gridTileSize;
        const gridHeight = GRID_HEIGHT * gridTileSize;
        const gridCenterX = gridMarginX + gridWidth / 2;
        const gridCenterY = gridMarginY + gridHeight / 2;

        // Calculate scale to fit grid on screen
        const scaleX = gameContainer.clientWidth / gridWidth;
        const scaleY = gameContainer.clientHeight / gridHeight;
        let scale = Math.min(scaleX, scaleY, 1);
        if (isXsScreen()) {
          scale = scale * 0.75;
        }

        //scale = scale * 1.25;

        // Apply scale and center the grid
        world.scale.set(scale);
        world.position.set(
          gameContainer.clientWidth / 2 - gridCenterX * scale,
          gameContainer.clientHeight / 2 - gridCenterY * scale,
        );

        if (overlayWorld) {
          overlayWorld.scale.set(scale);
          overlayWorld.position.set(
            gameContainer.clientWidth / 2 - gridCenterX * scale,
            gameContainer.clientHeight / 2 - gridCenterY * scale,
          );
        }

        // Instantiate and position pokemon at starting positions
        const newPokemons: Record<string, ClientPokemon> = {};
        const pokemonInitPromises: Promise<void>[] = [];

        // Create pokemon for Team A
        for (const member of battleLog.teamA.members) {
          const pokemon = new ClientPokemon(
            member.battleData,
            member.initialPosition,
            "A",
            app,
            world,
          );
          newPokemons[member.pokemon.id] = pokemon;

          const loadPromise = pokemon.load().then(() => {});
          pokemonInitPromises.push(loadPromise);
        }

        // Create pokemon for Team B
        for (const member of battleLog.teamB.members) {
          const pokemon = new ClientPokemon(
            member.battleData,
            member.initialPosition,
            "B",
            app,
            world,
          );
          newPokemons[member.pokemon.id] = pokemon;

          const loadPromise = pokemon.load().then(() => {});
          pokemonInitPromises.push(loadPromise);
        }

        // Initialize all pokemon in parallel
        await Promise.all(pokemonInitPromises);

        console.log("All pokemon initialized and positioned");
        setPokemons(newPokemons);
        setBattleLog(battleLog);

        // Go directly to battle (BattleStartingTransition handles the visual transition)
        console.log("Assets loaded, ready for battle");
        flushSync(() => {
          setScreen("battle");
        });
      } catch (error) {
        console.error("Failed to start battle:", error);
        // Could add error handling UI here
        setScreen("menu");
      }
    },
    [app, world, overlayWorld],
  );

  return (
    <main className="w-full h-full flex items-center justify-center flex-col bg-[#161e29]">
      <div className="relative w-full h-screen" id="game-container">
        <EnvironmentalEffects
          weather={"clouds"}
          className="opacity-70 !z-150"
        />
        <NightOverlay opacity={0.147} />

        {screen === "menu" && (
          <MenuScreen
            onStartBattle={handleStartBattle}
            isAppReady={!!(app && world)}
            isLocalTest={isLocalTest}
            battleLog={battleLog}
          />
        )}
        {screen === "battle" && app && battleLog && (
          <BattleScreen
            key={battleKey}
            pokemons={pokemons}
            battleLog={battleLog}
            isLocalTest={isLocalTest}
            onReplayBattle={async () => {
              // Clear world container (includes pokemon containers, map, overlay, grid)
              if (world) {
                while (world.children.length > 0) {
                  const child = world.children[0];
                  if (child) {
                    world.removeChild(child);
                    child.destroy({ children: true });
                  }
                }
              }
              if (overlayWorld) {
                while (overlayWorld.children.length > 0) {
                  const child = overlayWorld.children[0];
                  if (child) {
                    overlayWorld.removeChild(child);
                    child.destroy({ children: true });
                  }
                }
              }
              // Reset state and go to loading screen
              setPokemons({});
              setBattleStarted(false);
              setBattleKey((k) => k + 1);
              setScreen("loading");
              // Wait for battle setup to complete (it will setScreen("battle") at the end)
              await handleStartBattle(battleLog);
            }}
          />
        )}
      </div>
    </main>
  );
};

export default Battle;
